import React, { useState, useEffect } from 'react';
import { 
  CheckCircleIcon, 
  ExclamationTriangleIcon, 
  InformationCircleIcon,
  CubeIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import { AVAILABLE_MODULES } from '../../types/package';
import { validateModuleConsistency, getCoreModules } from '../../utils/moduleValidator';

const ModuleStatus: React.FC = () => {
  const [validationResult, setValidationResult] = useState<{
    isValid: boolean;
    errors: string[];
    warnings: string[];
  } | null>(null);

  useEffect(() => {
    const result = validateModuleConsistency();
    setValidationResult(result);
  }, []);

  if (!validationResult) {
    return (
      <div className="animate-pulse">
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-2"></div>
        <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
      </div>
    );
  }

  const coreModules = getCoreModules();
  const totalModules = AVAILABLE_MODULES.length;
  const modulesByCategory = AVAILABLE_MODULES.reduce((acc, module) => {
    if (!acc[module.category]) {
      acc[module.category] = [];
    }
    acc[module.category].push(module);
    return acc;
  }, {} as Record<string, typeof AVAILABLE_MODULES>);

  return (
    <div className="space-y-6">
      {/* Validation Status */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center space-x-3 mb-4">
          {validationResult.isValid ? (
            <CheckCircleIcon className="h-6 w-6 text-green-500" />
          ) : (
            <ExclamationTriangleIcon className="h-6 w-6 text-red-500" />
          )}
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            Modul konzisztencia
          </h3>
        </div>

        {validationResult.isValid && validationResult.warnings.length === 0 && (
          <div className="flex items-center space-x-2 text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/20 p-3 rounded-md border border-green-200 dark:border-green-800">
            <CheckCircleIcon className="h-5 w-5" />
            <span className="text-sm font-medium">Minden modul konzisztens és helyesen konfigurált!</span>
          </div>
        )}

        {validationResult.errors.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-medium text-red-800 dark:text-red-300 mb-2">Hibák:</h4>
            <ul className="space-y-1">
              {validationResult.errors.map((error, index) => (
                <li key={index} className="flex items-start space-x-2 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 p-2 rounded border border-red-200 dark:border-red-800">
                  <ExclamationTriangleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {validationResult.warnings.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-300 mb-2">Figyelmeztetések:</h4>
            <ul className="space-y-1">
              {validationResult.warnings.map((warning, index) => (
                <li key={index} className="flex items-start space-x-2 text-yellow-700 dark:text-yellow-300 bg-yellow-50 dark:bg-yellow-900/20 p-2 rounded border border-yellow-200 dark:border-yellow-800">
                  <InformationCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{warning}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Module Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center">
            <CubeIcon className="h-8 w-8 text-blue-500" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Összes modul</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{totalModules}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center">
            <ShieldCheckIcon className="h-8 w-8 text-green-500" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Core modulok</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{coreModules.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center">
            <InformationCircleIcon className="h-8 w-8 text-purple-500" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Kategóriák</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{Object.keys(modulesByCategory).length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Modules by Category */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Modulok kategóriák szerint</h3>
        </div>

        <div className="p-6 space-y-6">
          {Object.entries(modulesByCategory).map(([category, modules]) => (
            <div key={category}>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 capitalize">
                {category === 'crm' ? 'CRM' : 
                 category === 'products' ? 'Termékek' :
                 category === 'sales' ? 'Értékesítés' :
                 category === 'documents' ? 'Dokumentumok' :
                 category === 'projects' ? 'Projektek' :
                 category === 'finance' ? 'Pénzügy' :
                 category === 'admin' ? 'Adminisztráció' :
                 category === 'core' ? 'Alapfunkciók' : category}
                <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">({modules.length} modul)</span>
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {modules.map((module) => (
                  <div
                    key={module.id}
                    className={`flex items-center p-3 border rounded-lg ${
                      module.isCore 
                        ? 'border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20' 
                        : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50'
                    }`}
                  >
                    <CubeIcon className="h-5 w-5 text-gray-400 dark:text-gray-500 mr-3" />
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {module.name}
                        </span>
                        {module.isCore && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                            Core
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{module.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Core Modules Info */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <InformationCircleIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-blue-900 dark:text-blue-300">Core modulok</h4>
            <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">
              A core modulok ({coreModules.join(', ')}) minden csomagban automatikusan benne vannak és nem távolíthatók el.
              Ezek biztosítják a rendszer alapvető működését.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModuleStatus;
